import { isFetchBaseQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";

/**
 * ValidationError is the validation error as
 * returned by the backend.
 */
export interface ValidationError {
  message: string;
  errors: FieldErrors;
}

export function fieldErrors(error: unknown, field: string): string[] | null {
  if (!error) {
    return null;
  }

  if (!isValidationError(error)) {
    return null;
  }

  return error.errors[field] ?? null;
}

/**
 * fieldErrorsForPrefix extracts the field errors for with the given prefix.
 * @param error
 * @param prefix
 */
export function fieldErrorsForPrefix(
  error: ValidationError | null,
  prefix: string,
): FieldErrors | undefined {
  if (!error) {
    return undefined;
  }

  if (prefix === "") {
    return error.errors;
  }

  return Object.fromEntries(
    Object.entries(error.errors)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.slice(prefix.length), value]),
  );
}

export function isValidationError(error: unknown): error is ValidationError {
  // test if error is an object
  if (!error || typeof error !== "object") {
    return false;
  }

  // test if error has the required fields
  if (!("message" in error) || !("errors" in error)) {
    return false;
  }

  // test if the fields are of the correct type
  if (typeof error.message !== "string") {
    return false;
  }

  return isFieldErrors(error.errors);
}

export function rtkErrIsValidationError(
  error: unknown,
): error is { data: ValidationError } {
  if (!error) {
    return false;
  }

  if (!isFetchBaseQueryError(error)) {
    return false;
  }

  if (error.status !== 422) {
    return false;
  }

  return isValidationError(error.data);
}

export interface FieldErrors {
  [field: string]: string[];
}

export function isFieldErrors(errors: unknown): errors is FieldErrors {
  if (typeof errors !== "object" || errors === null) {
    return false;
  }

  return Object.entries(errors).every(
    ([, v]) => Array.isArray(v) && v.every((e) => typeof e === "string"),
  );
}

/**
 * mergeFieldErrors combines the field errors of several sources. Messages of
 * the same field are concatenated, duplicates are dropped.
 */
export function mergeFieldErrors(
  ...sources: (FieldErrors | undefined)[]
): FieldErrors {
  const merged: FieldErrors = {};
  for (const source of sources) {
    for (const [field, messages] of Object.entries(source ?? {})) {
      merged[field] = [...new Set([...(merged[field] ?? []), ...messages])];
    }
  }
  return merged;
}
